import { Fragment } from "react";
import { Box, Button, Text } from "@salestrak/ui";

import type { FilterBlock as FilterBlockType, FilterConfig, FilterValue } from "#app/lib/filters/types";
import type { IssueScope } from "#app/lib/filters/issue-scope";
import type { Omittable } from "@salestrak/ui";
import { FilterRow } from "./filter-row";
import { LogicSelect } from "./logic-select";

type FilterBlockProps = Omittable<{
  config: FilterConfig;
  block: FilterBlockType;
  onAddFilter: () => void;
  onRemoveFilter: (filterIndex: number) => void;
  onUpdateFilter: (filterIndex: number, update: Partial<FilterValue>) => void;
  onSetBlockLogic: (logic: "AND" | "OR") => void;
  onRemoveBlock: () => void;
  /** The screen's scope, handed to each row — see `FilterRow`. */
  scope?: IssueScope;
}>;

/**
 * A single filter block containing N filter rows.
 *
 * The block's AND/OR stands on its own short line between rows — the control
 * between the first two, the chosen word echoed between the rest — rather
 * than in a column beside a control, so the rows keep the full card width.
 * Renders in a card-like container with muted background.
 */
export function FilterBlock({
  config,
  block,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  onSetBlockLogic,
  onRemoveBlock,
  scope,
}: FilterBlockProps) {
  return (
    // Symmetric 8px inset — row controls land on the panel's 16px content
    // line (8px tab frame + this inset).
    <Box border rounded="lg" paddingX="sm" paddingY="sm">
      <Box gap="sm">
        {block.filters.map((filter, filterIndex) => (
          <Fragment key={filterIndex}>
            {filterIndex === 1 ? (
              <Box direction="row" align="center">
                <LogicSelect value={block.logic} onChange={onSetBlockLogic} />
              </Box>
            ) : filterIndex > 1 ? (
              // Inset to the select's own text so the echoed word sits under it.
              <Box direction="row" align="center" paddingX="sm">
                <Text size="sm" variant="muted" weight="medium">
                  {block.logic}
                </Text>
              </Box>
            ) : null}
            <FilterRow
              config={config}
              filter={filter}
              onUpdate={(update) => onUpdateFilter(filterIndex, update)}
              onRemove={() => onRemoveFilter(filterIndex)}
              scope={scope}
            />
          </Fragment>
        ))}

        {/* Footer actions */}
        <Box direction="row" align="center" gap="sm" justify="between">
          <Button variant="ghost" size="sm" onClick={onAddFilter}>
            <Text weight="medium">Add filter to group</Text>
          </Button>
          <Button variant="ghost" size="sm" onClick={onRemoveBlock}>
            <Text size="sm" variant="muted" weight="medium">Clear group</Text>
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
